import { useState, useEffect, useCallback } from 'react';
import { trpc } from '@lib/trpc';
import { useAuth } from '@contexts/AuthContext';
import type { PermissionCheck } from '@types';

// Cache for permission checks to avoid repeated API calls
const permissionCache = new Map<string, boolean>();

export const usePermissions = () => {
  const { user, isAuthenticated } = useAuth();

  /**
   * Check if the current user has a specific permission
   * @param permission - The permission to check (resource.action format)
   * @returns boolean indicating if user has permission
   */
  const hasPermission = useCallback(async (permission: PermissionCheck): Promise<boolean> => {
    if (!isAuthenticated || !user) {
      return false;
    }

    const cacheKey = `${user.id}-${permission.resource}-${permission.action}`;
    
    // Check cache first
    if (permissionCache.has(cacheKey)) {
      return permissionCache.get(cacheKey)!;
    }

    try {
      const result = await trpc.roles.checkPermission.query(permission);
      const hasPermissionResult = result?.data?.hasPermission || false;
      
      // Cache the result
      permissionCache.set(cacheKey, hasPermissionResult);
      
    } catch (error) {
      // Permission check error - return false
      return false;
    }
  }, [user, isAuthenticated]);

  /**
   * Clear permission cache (useful when user roles change)
   */
  const clearPermissionCache = useCallback(() => {
    permissionCache.clear();
  }, []);

  return {
    hasPermission,
    clearPermissionCache,
  };
};

/**
 * React hook for checking a single permission with reactive updates
 * @param permission - The permission to check
 * @returns object with permission state and loading state
 */
export const usePermission = (permission: PermissionCheck) => {
  const { user, isAuthenticated } = useAuth();
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Use tRPC query for reactive permission checking
  const { data, isLoading: queryLoading, error } = trpc.roles.checkPermission.useQuery(
    permission,
    {
      enabled: isAuthenticated && !!user,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    }
  );

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setHasPermission(false);
      setIsLoading(false);
      return;
    }

    if (queryLoading) {
      setIsLoading(true);
      return;
    }

    if (error) {
      // Permission check error - set to false
      setHasPermission(false);
      setIsLoading(false);
      return;
    }

    setHasPermission(data?.data?.hasPermission || false);
    setIsLoading(false);
  }, [user, isAuthenticated, data, queryLoading, error]);

  return {
    hasPermission,
    isLoading,
    error,
  };
};

/**
 * React hook for checking multiple permissions
 * @param permissions - Array of permissions to check
 * @returns object with permission states and loading state
 */
export const useMultiplePermissions = (permissions: PermissionCheck[]) => {
  const { user, isAuthenticated } = useAuth();
  const [permissionStates, setPermissionStates] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!isAuthenticated || !user || permissions.length === 0) {
      setPermissionStates({});
      setIsLoading(false);
      return;
    }

    const checkPermissions = async () => {
      setIsLoading(true);
      const states: Record<string, boolean> = {};

      try {
        // Check all permissions in parallel
        const results = await Promise.all(
          permissions.map(async (permission) => {
            try {
              const result = await trpc.roles.checkPermission.query(permission);
              return {
                key: `${permission.resource}.${permission.action}`,
                hasPermission: result?.data?.hasPermission || false,
              };
            } catch (error) {
              // Permission check error for specific resource/action - return false
              return {
                key: `${permission.resource}.${permission.action}`,
                hasPermission: false,
              };
            }
          })
        );

        results.forEach(({ key, hasPermission }) => {
          states[key] = hasPermission;
        });

      } catch (error) {
        // Multiple permissions check error - set all to false
        permissions.forEach((permission) => {
          states[`${permission.resource}.${permission.action}`] = false;
        });
        setPermissionStates(states);
      } finally {
        setIsLoading(false);
      }
    };

    checkPermissions();
  }, [user, isAuthenticated, permissions]);

  return {
    permissionStates,
    isLoading,
    hasPermission: (permission: PermissionCheck) => 
      permissionStates[`${permission.resource}.${permission.action}`] || false,
  };
};

// Helper function to create permission objects
export const createPermission = (resource: string, action: string): PermissionCheck => ({
  resource,
  action,
});

// Pre-defined permission constants for easy use
// These match the permissions defined in your database migration
export const SUPPLIER_PERMISSIONS = {
  CREATE: createPermission('supplier', 'create'),
  READ: createPermission('supplier', 'read'),
  UPDATE: createPermission('supplier', 'update'),
  DELETE: createPermission('supplier', 'delete'),
  EXPORT: createPermission('supplier', 'export'),
} as const;

export const INVOICE_PERMISSIONS = {
  CREATE: createPermission('invoice', 'create'),
  READ: createPermission('invoice', 'read'),
  UPDATE: createPermission('invoice', 'update'),
  DELETE: createPermission('invoice', 'delete'),
  STATUS: createPermission('invoice', 'status'),
  EXPORT: createPermission('invoice', 'export'),
} as const;

export const USER_PERMISSIONS = {
  CREATE: createPermission('user', 'create'),
  READ: createPermission('user', 'read'),
  UPDATE: createPermission('user', 'update'),
  DELETE: createPermission('user', 'delete'),
  ROLES: createPermission('user', 'roles'),
} as const;

export const SYSTEM_PERMISSIONS = {
  AUDIT: createPermission('system', 'audit'),
  SETTINGS: createPermission('system', 'settings'),
  BACKUP: createPermission('system', 'backup'),
} as const;

export const REPORTS_PERMISSIONS = {
  FINANCIAL: createPermission('reports', 'financial'),
  INVENTORY: createPermission('reports', 'inventory'),
  USER: createPermission('reports', 'user'),
} as const;

// Hook to get all available permissions from the database
export const useAvailablePermissions = () => {
  const { user, isAuthenticated } = useAuth();
  
  return trpc.roles.getAllPermissions.useQuery(undefined, {
    enabled: isAuthenticated && !!user,
    staleTime: 10 * 60 * 1000, // Consider fresh for 10 minutes
    cacheTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
  });
};
